import { z } from 'zod';
import {
  APP_PLATFORMS,
  BALANCE_BASES,
  BILLING_TYPES,
  GST_REGISTRATION_TYPES,
  PLAY_ACCOUNT_TYPES,
  PLAY_VERIFICATION_STATUSES,
  REVENUE_SOURCE_TYPES,
} from '../enums';
import { isIndianStateName, isValidGstin } from '../gstin';
import { isValidPackageName, parsePlayStorePackage } from '../playStore';
import {
  code,
  emptyAsNull,
  emptyAsUndefined,
  concurrency,
  currencyCode,
  exchangeRate,
  gstRatePercent,
  id,
  listQuery,
  moneyAmount,
  optionalEmail,
  optionalId,
  optionalText,
  accountStatus,
  appStatus,
  recordStatus,
  requiredId,
  sortOrder,
  text,
  ymd,
} from './common';

/*
 * Each master has a create schema (all required fields) and an update schema
 * (every field optional). No `.default()` here: in Zod 4 defaults also fire
 * inside optional fields, which would silently reset values on update.
 * Defaults are applied by the database / services instead.
 */

// ── Apps ─────────────────────────────────────────────────────────────
const appFields = {
  name: text(150, 'App name'),
  packageName: optionalText(191).refine(
    (v) => v == null || isValidPackageName(v),
    'Invalid package name (e.g. com.example.app)',
  ),
  platform: z.enum(APP_PLATFORMS),
  playStoreUrl: optionalText(500).refine(
    (v) => v == null || parsePlayStorePackage(v) !== null,
    'Enter a Google Play link like https://play.google.com/store/apps/details?id=com.example.app',
  ),
  developerName: optionalText(200),
  iconUrl: optionalText(1000).refine(
    (v) => v == null || /^https:\/\/[^\s]+$/.test(v),
    'Icon must be an https:// URL',
  ),
  storeCategory: optionalText(80),
  developerAccessEmail: optionalEmail,
  firebaseAccountId: optionalId,
  firebaseProjectId: optionalText(100),
  /** Google Play developer account that publishes the app. */
  playDeveloperAccountId: optionalId,
  status: appStatus.optional(),
  description: optionalText(5000),
  notes: optionalText(5000),
};
export const appCreateSchema = z.object(appFields);
export const appUpdateSchema = z.object(appFields).partial().extend(concurrency);
export const appListSchema = listQuery.extend({
  status: appStatus.optional(),
  platform: z.enum(APP_PLATFORMS).optional(),
});
export const playStoreLookupSchema = z.object({
  url: z
    .string({ error: 'Google Play link is required' })
    .trim()
    .min(1, 'Google Play link is required')
    .max(500)
    .refine(
      (v) => parsePlayStorePackage(v) !== null,
      'Not a valid Google Play app link or package name',
    ),
});
export type AppCreateInput = z.output<typeof appCreateSchema>;
export type AppUpdateInput = z.output<typeof appUpdateSchema>;

// ── Advertising platforms ────────────────────────────────────────────
const advertisingPlatformFields = {
  name: text(100, 'Name'),
  code: code(),
  status: recordStatus.optional(),
  sortOrder: sortOrder.optional(),
};
export const advertisingPlatformCreateSchema = z.object(advertisingPlatformFields);
export const advertisingPlatformUpdateSchema = z
  .object(advertisingPlatformFields)
  .partial()
  .extend(concurrency);
export const advertisingPlatformListSchema = listQuery.extend({ status: recordStatus.optional() });

// ── GST accounts ─────────────────────────────────────────────────────
const gstin = z
  .string({ error: 'GSTIN is required' })
  .trim()
  .toUpperCase()
  .refine(isValidGstin, 'Invalid GSTIN (format or checksum)');
const gstAccountFields = {
  gstin,
  legalName: text(200, 'Legal name'),
  tradeName: optionalText(200),
  /** Optional: derived from the GSTIN state code; if sent it must match. */
  state: z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    z.string().refine(isIndianStateName, 'Select a state from the list').optional(),
  ),
  registrationType: z.enum(GST_REGISTRATION_TYPES),
  defaultGstRate: gstRatePercent,
  status: recordStatus.optional(),
  notes: optionalText(5000),
};
export const gstAccountCreateSchema = z.object(gstAccountFields);
export const gstAccountUpdateSchema = z.object(gstAccountFields).partial().extend(concurrency);
export const gstAccountListSchema = listQuery.extend({
  status: recordStatus.optional(),
  state: z.string().trim().max(80).optional(),
});
export type GstAccountCreateInput = z.output<typeof gstAccountCreateSchema>;

// ── GST rates ────────────────────────────────────────────────────────
const gstRateFields = {
  rate: gstRatePercent,
  label: text(40, 'Label'),
  isDefault: z.boolean().optional(),
  status: recordStatus.optional(),
};
export const gstRateCreateSchema = z.object(gstRateFields);
export const gstRateUpdateSchema = z.object(gstRateFields).partial().extend(concurrency);
export const gstRateListSchema = listQuery.extend({ status: recordStatus.optional() });

// ── Currencies ───────────────────────────────────────────────────────
const currencyFields = {
  code: currencyCode,
  name: text(60, 'Name'),
  symbol: text(8, 'Symbol'),
  decimalPlaces: z.coerce.number().int().min(0).max(4),
  status: recordStatus.optional(),
};
export const currencyCreateSchema = z.object(currencyFields);
export const currencyUpdateSchema = z
  .object(currencyFields)
  .omit({ code: true })
  .partial()
  .extend(concurrency);
export const currencyListSchema = listQuery.extend({ status: recordStatus.optional() });

// ── Currency rates (monthly) ─────────────────────────────────────────
const year = z.coerce.number().int().min(2000).max(2100);
const month = z.coerce.number().int().min(1).max(12);
export const currencyRateCreateSchema = z.object({
  currencyCode,
  year,
  month,
  exchangeRateToInr: exchangeRate,
  notes: optionalText(5000),
});
/** Only the rate and notes can change; currency/year/month identify the row. */
export const currencyRateUpdateSchema = z
  .object({ exchangeRateToInr: exchangeRate, notes: optionalText(5000) })
  .partial()
  .extend(concurrency);
export const currencyRateListSchema = listQuery.extend({
  currencyCode: currencyCode.optional(),
  year: year.optional(),
});
export const currencyRateBulkSchema = z.object({
  rates: z
    .array(
      z.object({
        currencyCode,
        year,
        month,
        exchangeRateToInr: exchangeRate,
        notes: optionalText(5000),
      }),
    )
    .min(1)
    .max(500),
});
export type CurrencyRateBulkInput = z.output<typeof currencyRateBulkSchema>;

// ── Advertising accounts ─────────────────────────────────────────────
const advertisingAccountFields = {
  advertisingPlatformId: requiredId('Platform'),
  accountName: text(150, 'Account name'),
  accountIdentifier: optionalText(100),
  currencyCode,
  gstAccountId: optionalId,
  defaultGstRate: emptyAsNull(gstRatePercent),
  loginEmail: optionalEmail,
  accessEmail: optionalEmail,
  billingType: z.enum(BILLING_TYPES).optional(),
  managerAccountId: optionalId,
  openingBalance: emptyAsUndefined(moneyAmount),
  openingBalanceDate: emptyAsNull(ymd),
  balanceBasis: z.enum(BALANCE_BASES).optional(),
  status: accountStatus.optional(),
  sortOrder: sortOrder.optional(),
  notes: optionalText(5000),
};
export const advertisingAccountCreateSchema = z.object(advertisingAccountFields);
export const advertisingAccountUpdateSchema = z
  .object(advertisingAccountFields)
  .partial()
  .extend(concurrency);
export const advertisingAccountListSchema = listQuery.extend({
  status: accountStatus.optional(),
  appId: id.optional(),
  advertisingPlatformId: id.optional(),
  gstAccountId: id.optional(),
  managerAccountId: id.optional(),
  billingType: z.enum(BILLING_TYPES).optional(),
  platformCode: code().optional(),
  notAssignedToAppId: id.optional(),
});

// ── Manager accounts (MCC) ───────────────────────────────────────────
const managerAccountFields = {
  name: text(150, 'Name'),
  advertisingPlatformId: requiredId('Platform'),
  gstAccountId: requiredId('GST account'),
  accountIdentifier: optionalText(100),
  loginEmail: optionalEmail,
  currencyCode,
  creditLimit: emptyAsNull(moneyAmount),
  balanceBasis: z.enum(BALANCE_BASES).optional(),
  openingBalanceDate: emptyAsNull(ymd),
  openingUsedAmount: emptyAsUndefined(moneyAmount),
  status: recordStatus.optional(),
  notes: optionalText(5000),
};
export const managerAccountCreateSchema = z.object(managerAccountFields);
export const managerAccountUpdateSchema = z
  .object(managerAccountFields)
  .partial()
  .extend(concurrency);
export const managerAccountListSchema = listQuery.extend({
  status: recordStatus.optional(),
  advertisingPlatformId: id.optional(),
  gstAccountId: id.optional(),
});

// ── Firebase accounts ────────────────────────────────────────────────
const firebaseAccountFields = {
  name: text(150, 'Name'),
  loginEmail: optionalEmail,
  accessEmail: optionalEmail,
  status: recordStatus.optional(),
  sortOrder: sortOrder.optional(),
  notes: optionalText(5000),
};
export const firebaseAccountCreateSchema = z.object(firebaseAccountFields);
export const firebaseAccountUpdateSchema = z
  .object(firebaseAccountFields)
  .partial()
  .extend(concurrency);
export const firebaseAccountListSchema = listQuery.extend({
  status: recordStatus.optional(),
});

// ── Google Play developer accounts ───────────────────────────────────
const optionalUrl = optionalText(500).refine(
  (v) => v == null || /^https?:\/\/[^\s]+$/.test(v),
  'Enter a link starting with https://',
);
const playDeveloperAccountFields = {
  /** Developer name as shown on Google Play. */
  name: text(200, 'Developer name'),
  developerId: optionalText(40),
  accountType: emptyAsNull(z.enum(PLAY_ACCOUNT_TYPES)),
  loginEmail: optionalEmail,
  accessEmail: optionalEmail,
  openedOn: emptyAsNull(ymd),
  openedOnDevice: optionalText(150),
  verificationDevice: optionalText(150),
  verificationPhone: optionalText(30),
  verificationStatus: emptyAsNull(z.enum(PLAY_VERIFICATION_STATUSES)),
  verifiedOn: emptyAsNull(ymd),
  accessDevice: optionalText(150),
  accessInternet: optionalText(150),
  legalName: optionalText(200),
  contactEmail: optionalEmail,
  phone: optionalText(50),
  address: optionalText(2000),
  website: optionalUrl,
  status: accountStatus.optional(),
  sortOrder: sortOrder.optional(),
  notes: optionalText(5000),
};
export const playDeveloperAccountCreateSchema = z.object(playDeveloperAccountFields);
export const playDeveloperAccountUpdateSchema = z
  .object(playDeveloperAccountFields)
  .partial()
  .extend(concurrency);
export const playDeveloperAccountListSchema = listQuery.extend({
  status: accountStatus.optional(),
  verificationStatus: z.enum(PLAY_VERIFICATION_STATUSES).optional(),
});

// ── Revenue sources ──────────────────────────────────────────────────
const revenueSourceFields = {
  name: text(100, 'Name'),
  code: code(),
  type: z.enum(REVENUE_SOURCE_TYPES),
  status: recordStatus.optional(),
  description: optionalText(5000),
  sortOrder: sortOrder.optional(),
};
export const revenueSourceCreateSchema = z.object(revenueSourceFields);
export const revenueSourceUpdateSchema = z
  .object(revenueSourceFields)
  .partial()
  .extend(concurrency);
export const revenueSourceListSchema = listQuery.extend({
  status: recordStatus.optional(),
  type: z.enum(REVENUE_SOURCE_TYPES).optional(),
});

// ── Revenue accounts ─────────────────────────────────────────────────
const revenueAccountFields = {
  revenueSourceId: requiredId('Revenue source'),
  accountName: text(150, 'Account name'),
  accountIdentifier: optionalText(100),
  loginEmail: optionalEmail,
  accessEmail: optionalEmail,
  currencyCode,
  gstAccountId: optionalId,
  /** Minimum payout; '' / null = pays every month (on create: the network's standard one). */
  payoutThreshold: emptyAsNull(moneyAmount),
  status: accountStatus.optional(),
  sortOrder: sortOrder.optional(),
  notes: optionalText(5000),
};
export const revenueAccountCreateSchema = z.object(revenueAccountFields);
export const revenueAccountUpdateSchema = z
  .object(revenueAccountFields)
  .partial()
  .extend(concurrency);
export const revenueAccountListSchema = listQuery.extend({
  status: accountStatus.optional(),
  /** Accounts currently assigned to this app. */
  appId: id.optional(),
  revenueSourceId: id.optional(),
  /** e.g. ADMOB, ADX — used by the AdMob / AdX account pages. */
  sourceCode: code().optional(),
  /** Exclude accounts currently assigned to this app (assignment pickers). */
  notAssignedToAppId: id.optional(),
});

// ── Expense categories ───────────────────────────────────────────────
const expenseCategoryFields = {
  name: text(100, 'Name'),
  code: code(),
  status: recordStatus.optional(),
  sortOrder: sortOrder.optional(),
};
export const expenseCategoryCreateSchema = z.object(expenseCategoryFields);
export const expenseCategoryUpdateSchema = z
  .object(expenseCategoryFields)
  .partial()
  .extend(concurrency);
export const expenseCategoryListSchema = listQuery.extend({ status: recordStatus.optional() });
